import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';

import Button from '../../../../components/Button/Button';
import { IconReload } from '../../../../assets/icon';
import { ReactComponent as IconFilter } from '../../../../assets/icon/ic_filter.svg';
import ButtonTypes from '../../../../constants/buttonTypes';
import { setWarehouseFilter } from '../../../../actions/coilovers/warehouse/warehouseFilter';
import getWarehouse from '../../../../thunks/coilovers/warehouse/getWarehouse';
import PermissionsGate from '../../../../components/PermissionsGate/PermissionsGate';
import { permissions } from '../../../../common/permissionGroup';
import CustomDropdownMenu from '../../../../components/CustomDropdownMenu/CustomDropdownMenu';
import CustomSelect from '../../../../components/Select/CustomSelect';
import Text from '../../../../components/Text/Text';
import getBrandList from '../../../../thunks/general/getBrandList';

const selectContainerClassName = 'w-[300px] max-w-[calc(100vw-48px)]';
const labelContainerClassName = 'mb-[6px]';
const labelTextClassName = 'text-[13px]';
const filterFooterClassName = 'mt-[18px] flex justify-end gap-[10px]';

const WarehouseHeaderRight = ({ cartCount, onOpenSupplierOrder }) => {
  const dispatch = useDispatch();
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const {
    warehouseFilter: {
      needPartsToOrder,
      needToOrder,
      order,
      query,
      brands,
    },
    brandList,
    brandListLoading,
    brandListError,
  } = useSelector((state) => ({
    warehouseFilter: state?.warehouseFilter,
    brandList: state?.getBrandList?.data || [],
    brandListLoading: state?.getBrandList?.loading,
    brandListError: state?.getBrandList?.error,
  }), shallowEqual);

  useEffect(() => {
    if (!brandList.length && !brandListLoading && !brandListError) {
      dispatch(getBrandList());
    }
  }, [brandList.length, brandListLoading, brandListError, dispatch]);

  useEffect(() => {
    setSelectedBrands((brands || []).map((brand) => ({ label: brand, value: brand })));
  }, [brands]);

  const onApplyBrands = () => {
    batch(() => {
      dispatch(setWarehouseFilter({
        brands: selectedBrands.map(({ value }) => value),
        page: 1,
      }));
      dispatch(getWarehouse());
    });
    setMenuVisible(false);
  };

  const onResetBrands = () => {
    setSelectedBrands([]);
    batch(() => {
      dispatch(setWarehouseFilter({ brands: undefined, page: 1 }));
      dispatch(getWarehouse());
    });
    setMenuVisible(false);
  };

  const onClick = () => {
    setSelectedBrands([]);
    dispatch(setWarehouseFilter({
      needPartsToOrder: undefined,
      needToOrder: undefined,
      order: undefined,
      query: '',
      brands: undefined,
    }));
    dispatch(getWarehouse());
  };
  return (
    <div className="flex flex-wrap items-center justify-end gap-[10px]">
      <CustomDropdownMenu
        Icon={IconFilter}
        btnText="Brand"
        btnType={ButtonTypes.LIGHT_PRIMARY}
        headerText="Filter by brand"
        show={menuVisible}
        onToggle={setMenuVisible}
        indicator={!!brands?.length}
      >
        <div className={selectContainerClassName}>
          <Text text="Brands:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
          <CustomSelect
            value={selectedBrands}
            options={brandList}
            placeholder={brandListLoading ? 'Loading...' : 'Select brands'}
            isDisabled={brandListLoading}
            isLoading={brandListLoading}
            isClearable
            isSearchable
            isMulti
            closeMenuOnSelect={false}
            maxSelectedValue={brandList.length || 1000}
            onChange={(values) => setSelectedBrands(values || [])}
          />
        </div>
        <div className={filterFooterClassName}>
          <Button text="Reset" type={ButtonTypes.LIGHT} onClick={() => onResetBrands()} />
          <Button text="Apply" onClick={onApplyBrands} />
        </div>
      </CustomDropdownMenu>
      <PermissionsGate can={permissions.SUPPLIER_ORDERS_CREATE}>
        <div className="flex flex-wrap items-center justify-end gap-[10px]">
          <Button
            text={`Supplier order (${cartCount})`}
            type={cartCount ? ButtonTypes.LIGHT_SUCCESS : ButtonTypes.LIGHT}
            disabled={!cartCount}
            onClick={onOpenSupplierOrder}
          />
        </div>
      </PermissionsGate>
      {(needPartsToOrder || needToOrder || order || query || brands?.length)
        && <Button icon={<IconReload />} type={ButtonTypes.LIGHT} onClick={onClick} />}
    </div>
  );
};

WarehouseHeaderRight.propTypes = {
  cartCount: PropTypes.number,
  onOpenSupplierOrder: PropTypes.func,
};

WarehouseHeaderRight.defaultProps = {
  cartCount: 0,
  onOpenSupplierOrder: () => {},
};

export default WarehouseHeaderRight;
